import { regionalMarkets } from "../constants/lists";
import { rolesMap } from "../constants/roles-constants";
import { countryList } from "../constants/lists";
import { campaignsQueryParams } from "../types/campaign";

// Get the maximum role of the user
const getUserMaxRole = () => {
    const role = localStorage.getItem('userPreferredRole')
    return !isNaN(rolesMap[role]) ? rolesMap[role] : -1;
};

const getUserMarket = () => {
    const markets = localStorage.getItem('userMarket');
    return markets && markets !== 'undefined' ? markets.split(',').map(item => item.trim()) : [];
};

const getCampaignQueryParams = (): campaignsQueryParams => {
    const params: campaignsQueryParams = {
        filterInput: '',
        // if the user is not assigned a new role, return all campaigns to maintain backward compatibility.
        markets: getAllowedMarkets()
    };

    return params;
}

/**
 * Craete market-region map
 */
const marketToRegionMap = Object.entries(regionalMarkets)
    .reduce((acc, [region, markets]) => {
        markets.forEach(market => {
            acc[market] = region;
        });
        return acc;
    }, {});

/**
 * Validates the provided markets and resolves their region.
 * Returns the detected regions if valid.
 */
const getValidatedRegions = (
    markets: string[]
): string[] => {
    const regions: string[] = [];

    if (!markets) {
        return [];
    }

    for (const market of markets) {
        const region = marketToRegionMap[market];

        if (!region) {
            console.error(`Error: ${market} is not found in any region.`);
            continue;
        }

        regions.push(region);
    }

    return [...new Set(regions)];
};

/**
 * Get the list of markets based on the user's maximum role and assigned markets.
 *  *
 * @param {number} userMaxRole - The maximum role of the user, represented as a number.
 * @param {string[]} userMarket - The list of markets assigned to the user.
 * @returns {string[]} - The filtered list of markets the user has access to based on their role.
 *
 * - If the user has the `RegionalAdmin` role, all markets in the user's region are returned.
 * - If the user has the `PromotionManager` role, only the user's assigned markets are returned.
 * - If the user's markets are invalid (e.g., across multiple regions), an array with a null entry is returned and error will be logged in the console.
 */
const getAllowedMarkets = (): string[] => {
    const userMaxRole = getUserMaxRole();
    const userMarkets = getUserMarket();

    // SuperAdmins & Backward compatibility for no new role assigned : return all markets
    if (userMaxRole <= 0) {
        return Object.keys(countryList);
    }

    // not showing campaigns if no markets assigned for role-based users
    if (!userMarkets || userMarkets.length === 0) {
        console.error(
            'Error: No markets have been assigned to the user. Please contact the Super Admin to adjust the user permissions.'
        );
        return [null];
    }

    // RegionalAdmins: return regional markets
    if (userMaxRole === rolesMap.RegionalAdmin) {
        const regions = getValidatedRegions(userMarkets);

        if (!regions.length) {
            return [null];
        }

        if (regions.length > 1) {
            console.error(
                'Error: Markets across multiple regions have been assigned to the user. Please contact the Super Admin to adjust the user permissions.'
            );
            return [null];
        }

        return regionalMarkets[regions[0]];
    }

    // PromotionManagers and below: restricted to assigned markets
    if (userMaxRole >= rolesMap.PromotionManager) {
        return userMarkets;
    }

    return [null];
};

const getAllowedCountriesMap = (): { [key: string]: string } => {
    let countriesMap = {};
    const markets = getAllowedMarkets();

    for (const market of markets) {
        const country = countryList[market];
        if (country) {
            countriesMap[market] = country;
        }
    }

    return countriesMap;
};

export {
    getUserMaxRole,
    getUserMarket,
    getCampaignQueryParams,
    getAllowedMarkets,
    getAllowedCountriesMap,
};
